import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { TOKEN } from "shared/constants/LocalStorageConsts";
import { logout } from "shared/helpers/auth-functions";
import snakeToCamelCase from "shared/helpers/snakeToCamelCase";
import i18n from "i18next";

export const instance = axios.create({
  baseURL: process.env.REACT_APP_RADAR_ENDPOINT,
  timeout: 180000,
});

instance.interceptors.request.use((config: AxiosRequestConfig) => {
  config.headers["Authorization"] = `Bearer ${localStorage.getItem(TOKEN)}`;
  config.headers["Language"] = i18n.language;
  return config;
});

instance.interceptors.response.use(
  (response: AxiosResponse<any>) => snakeToCamelCase(response.data, true),
  (error: any) => {
    switch (error.response.status) {
      case 401:
      case 403:
        logout();
    }
  }
);

const noCamelCaseTransform = () => {
  const cloneInstance = axios.create({
    baseURL: process.env.REACT_APP_RADAR_ENDPOINT,
    timeout: 180000,
  });
  cloneInstance.interceptors.request.use((config: AxiosRequestConfig) => {
    config.headers["Authorization"] = `Bearer ${localStorage.getItem(TOKEN)}`;
    config.headers["Language"] = i18n.language;
    return config;
  });
  cloneInstance.interceptors.response.use(
    (response: AxiosResponse<any>) => response.data
  );
  return cloneInstance;
};

const stringArrayToqueryString = (array: string[], paramName: string) => {
  return array.map((item) => `${paramName}=${item}`).join("&");
};

export const fetchFilterCountries: any = () =>
  instance.get("filter/country-list");

export const fetchFilterGenres: any = () => instance.get("filter/genre-list");

export const fetchFilterGenresSpotify: any = () =>
  instance.get("filter/genre-spotify-list");

export const fetchHighlightedArtists: any = (params: any) =>
  instance.post("artistranking/get-list-highlighted", params);

export const fetchArtistGraph: any = (params: any, sortMode: string) =>
  noCamelCaseTransform().post(
    `artistranking/get-artist-network/${sortMode}`,
    params
  );

export const fetchArtistByName: any = (params: any) =>
  instance.post(`artist/search-by-name`, params);

export const fetchArtistRankingDetail: any = (id: string) =>
  instance.get(`artist/artist-rank-detail/${id}`);

export const compareArtists: any = (artists: any) =>
  instance.post("artist/compare-artists", artists);

export const fetchLatestRelease = (artistId: string) =>
  instance.get(`artist/artist-last-releases/${artistId}`);

export const fetchIndividualMetrics: any = (artistId: string) =>
  instance.get(`artist/artist-individual-metrics/${artistId}`);

export const fetchArtistPositionByFilter = (artistId: string, filter: any) =>
  instance.post(
    `artistranking/get-artist-position-rank-by-filter/${artistId}`,
    filter
  );

export const fetchTopSpotifyGenre = (monthQuantity: string, size: number) =>
  instance.get(`trend/genres-artist/${monthQuantity}/${size}`);

export const fetchAudioFeaturesTrend = (monthQuantity: number, size: number) =>
  instance.get(`trend/audio-features/${monthQuantity}/${size}`);

export const fetchGenreAudioFeaturesTrend = (monthQuantity: number) =>
  instance.get(`trend/audio-features-genres/${monthQuantity}`);

export const fetchArtistIndividualMetricLatestRelease: any = (id: string) =>
  instance.get(`artist/artist-individual-metrics-last-releases/${id}`);

export const fetchLabelRankingSlidersFilters = () =>
  instance.get("filter/label-sliders-size");

export const fetchArtistRank: any = (params: any) =>
  instance.post("artistranking/get-list", params);

export const exportArtistRankingCSV = (params: any) =>
  instance.post(`artistranking/export-csv-list`, params);

export const exportArtistRankingExcel = (params: any) =>
  instance.post(`artistranking/export-excel-list`, params, {
    responseType: "arraybuffer",
  });

export const fetchReleaseRanking: any = (params: any) =>
  instance.post("releaseranking/get-list", params);

export const exportReleaseRankingCSV = (params: any) =>
  instance.post(`releaseranking/export-csv-list`, params);

export const exportReleaseRankingExcel = (params: any) =>
  instance.post(`releaseranking/export-excel-list`, params, {
    responseType: "arraybuffer",
  });

export const fetchTrackRanking: any = (params: any) =>
  instance.post("tracksranking/get-list", params);

export const exportTrackRankingCSV = (params: any) =>
  instance.post(`tracksranking/export-csv-list`, params);

export const exportTrackRankingExcel = (params: any) =>
  instance.post(`tracksranking/export-excel-list`, params, {
    responseType: "arraybuffer",
  });

export const fetchLabelRanking = (params: any) =>
  instance.post("labelranking/get-list", params);

export const exportLabelRankingCSV = (params: any) =>
  instance.post(`labelranking/export-csv-list`, params);

export const exportLabelRankingExcel = (params: any) =>
  instance.post(`labelranking/export-excel-list`, params, {
    responseType: "arraybuffer",
  });

export const fetchLabelRankingChart: any = (params: any) =>
  instance.post("labelranking/get-chart-data", params);

export const fetchRoleRanking = (params: any) =>
  instance.post("roleranking/get-list", params);

export const fetchLabelByName = (name: string) =>
  instance.get(`filter/label-name-list/${name}`);

export const fetchMusicByName = (name: string) =>
  instance.get(`filter/track-name-list/${name}`);

export const fetchReleaseRankingSlidersFilters = () =>
  instance.get("filter/release-ranking-sliders-size");

export const fetchReleaseListByName = (name: string) =>
  instance.get(`filter/release-name-list/${name}`);

export const fetchTracksRankingSlidersFilters = () =>
  instance.get("filter/track-ranking-sliders-size");

export const fetchArtistReleasePolynominalTrend: any = (artistId: string) =>
  instance.get(`artist/artist-releases-polynomial-curve-trend/${artistId}`);

export const fetchArtistTrackPolynominalTrend: any = (artistId: string) =>
  instance.get(`artist/artist-tracks-polynomial-curve-trend/${artistId}`);

export const fetchArtistRevenueForecast: any = (
  artistId: string,
  currency: string
) => instance.get(`artist/revenue/${artistId}/${currency}`);

export const fetchLabelInfo: any = (params: any) =>
  instance.post(`label/get-info/`, params);

export const fetchLabelDetails: any = (params: any) =>
  instance.post(`label/get-details`, params);

export const fetchArtistTopCities: any = (id: string) =>
  instance.get(`Artist/artist-top-cities/${id}`);

export const fetchArtistDetails: any = (id: string) =>
  instance.get(`artist/detail/${id}`);

export const exportArtistRevenueCSV = (params: any) =>
  instance.post(`artist/revenue-export-csv`, params);

export const exportArtistRevenueExcel = (params: any) =>
  instance.post(`artist/revenue-export-excel`, params, {
    responseType: "arraybuffer",
  });

export const fetchTrackPopularityHistory: any = (ids: any) =>
  instance.get(
    `tracks/popularity-history?${stringArrayToqueryString(ids, "tracks")}`
  );

export const fetchSuportedDistros = () =>
  instance.get("filter/supported-distros");

export const fetchTotalMonitoredArtists = () =>
  instance.get(`ArtistRanking/total-monitored-artists`);
